import React from 'react'
import {FormattedMessage} from 'react-intl'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {ButtonLink} from '@vio/atlas/ButtonLink'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'

interface BreadcrumbItem {
  name: string
  path?: string
}

const BreadcrumbNavWrapper = styled.nav`
  ${linariaMq.desktopXs} {
    padding: ${cssTheme.layout.spacing.s400};
  }
`
const BreadcrumbList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`

const BreadcrumbItem = styled.li`
  display: inline-block;
  align-items: center;
  margin-right: ${cssTheme.layout.spacing.s200};
  margin-bottom: ${cssTheme.layout.spacing.s100};
  &:last-of-type {
    &:after {
      display: none;
    }
  }
  &:after {
    content: '/';
    display: inline-block;
    margin-left: ${cssTheme.layout.spacing.s200};
    color: ${cssTheme.colors.content.neutral.disabled};
    ${cssTheme.typography.titleXS}
  }

  &::first-letter {
    text-transform: uppercase;
  }

  &.hidden {
    display: none;
  }
`

// Only show the 3 most specific levels on mobile (rightmost items)
const MAX_MOBILE_LEVELS = 3

interface Props {
  breadcrumb: BreadcrumbItem[]
  className?: string
}

export const SeoBreadcrumb: React.FC<Props> = ({breadcrumb, className}) => {
  const {isMobile} = useDeviceLayout()

  if (!breadcrumb.length) {
    return null
  }

  const shouldHideHomePageLink =
    isMobile && breadcrumb.length >= MAX_MOBILE_LEVELS

  return (
    <BreadcrumbNavWrapper aria-label="breadcrumb" className={className}>
      <BreadcrumbList>
        <BreadcrumbItem className={cx({hidden: shouldHideHomePageLink})}>
          <ButtonLink href="/" size="sm" isCompact>
            <FormattedMessage id="seoMeta.home" defaultMessage="Home" />
          </ButtonLink>
        </BreadcrumbItem>

        {breadcrumb.map((item, index) => (
          <BreadcrumbItem
            key={`${item.name}-${index}`}
            className={cx({
              hidden:
                isMobile && index <= breadcrumb.length - 1 - MAX_MOBILE_LEVELS
            })}
          >
            {index === breadcrumb.length - 1 ? (
              <Text variant="bodyXS">{item.name}</Text>
            ) : (
              <ButtonLink href={item.path} size="sm" isCompact>
                {item.name}
              </ButtonLink>
            )}
          </BreadcrumbItem>
        ))}
      </BreadcrumbList>
    </BreadcrumbNavWrapper>
  )
}
